import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
  BreadcrumbHeading,
  Chip,
  SoftButton,
  InfoCard,
  InfoCardDetail,
  IOption,
  LinkButton,
  MultiSelect,
  Plus,
  PriceBandIcon,
  SearchField,
  Spinner,
  Typography,
  ViewIcon,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useTimetables } from 'src/hooks/useTimetables';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { ITimetable } from 'src/api/getTimetables';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { CreateTimetableModal } from 'src/components/CreateTimetableModal';
import styles from './Timetables.module.scss';

export const Timetables = () => {
  const navigate = useNavigate();
  const { trackEvent } = useUserTracking();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [selectedRangeIds, setSelectedRangeIds] = useState<string[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const rangeIds = selectedRangeIds.length > 0 ? selectedRangeIds : undefined;

  const { data: ranges } = useRanges();
  const rangeFilterOptions = useMemo(
    () => ranges?.map((range, index) => ({ id: range.range_id, label: range.name, display_order: index })) ?? [],
    [ranges],
  );
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useTimetables(
    15,
    debouncedSearchQuery,
    rangeIds,
  );

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage) fetchNextPage();
    },
  });

  useEffect(() => {
    document.title = 'Timetables';
    trackEvent('page_viewed', { page: 'timetables' });
  }, [trackEvent]);

  const timetables = data?.pages.flatMap((page) => page.data) ?? [];
  const totalCount = data?.pages[0]?.total_count ?? 0;
  const hasActiveFilters = !!debouncedSearchQuery || !!rangeIds;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const getCardDetails = (timetable: ITimetable): InfoCardDetail[] => [
    { text: 'Menus used', detail: String(timetable.menus.length) },
    { text: 'Price Schemes used', detail: String(timetable.price_schemes.length) },
  ];

  const getEllipsisOptions = (timetable: ITimetable): IOption[] => [
    {
      id: 'view',
      name: 'View',
      icon: ViewIcon,
      onOptionClick: () => navigate(`${Routes.TIMETABLES}/${timetable.timetable_id}`),
    },
  ];

  const getCountText = (): string => {
    if (isLoading) return 'Loading...';
    if (timetables.length > 0) return `${totalCount} ${pluralize('timetable', totalCount)}`;
    if (hasActiveFilters) return 'No results for your search...';
    return 'Build your first timetable...';
  };

  const getCardFootnote = (timetable: ITimetable) => {
    if (timetable.ranges.length === 0) return 'No Ranges';

    return (
      <>
        {timetable.ranges.map((range) => (
          <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
        ))}
      </>
    );
  };

  return (
    <div className={styles['page-container']}>
      <div className={styles['page-header']}>
        <BreadcrumbHeading breadcrumbs={[{ label: 'Timetables', path: Routes.TIMETABLES }]} />
        <div className={styles['filters']}>
          <SearchField placeholder="Search for a Timetable" onChange={handleSearchChange} isDisabled={false} />
          {rangesEnabled && (
            <MultiSelect
              Icon={PriceBandIcon}
              label="Range"
              options={rangeFilterOptions}
              selected={selectedRangeIds}
              onChange={setSelectedRangeIds}
            />
          )}
        </div>
        <Typography>{getCountText()}</Typography>
      </div>
      <SoftButton colour="blueberry" icon={Plus} size="full-width" onClick={() => setIsCreateModalOpen(true)}>
        Create Timetable
      </SoftButton>
      <CreateTimetableModal open={isCreateModalOpen} closeModal={() => setIsCreateModalOpen(false)} />
      {(isLoading || timetables.length > 0) && (
        <div className={styles['list']}>
          {isLoading && <SkeletonContainer hiddenTitle />}
          {!isLoading &&
            timetables.map((timetable) => (
              <div key={timetable.timetable_id}>
                <InfoCard
                  label="Menu & pricing timetable"
                  labelColour="blue"
                  title={timetable.name}
                  details={getCardDetails(timetable)}
                  footnote={getCardFootnote(timetable)}
                  ellipsisOptions={getEllipsisOptions(timetable)}
                  action={
                    <LinkButton onClick={() => navigate(`${Routes.TIMETABLES}/${timetable.timetable_id}`)}>
                      View Timetable
                    </LinkButton>
                  }
                />
              </div>
            ))}
          {isFetchingNextPage && (
            <div className={styles['spinner-container']}>
              <Spinner size="medium" colour="rainbow" />
            </div>
          )}
          {hasNextPage && !isFetchingNextPage && <div ref={ref} />}
        </div>
      )}
    </div>
  );
};

export default Timetables;
